import { ReactNode, memo } from 'react';
import { Layout, Form, FormProps } from 'antd';
import classNames from 'classnames';
import { User, RequisitionInfo } from 'types';
import { FooterActions } from '@Enum/index';
import { Informative, MTextArea } from '@Components/Base';
import { ReactComponent as NextArrowIcon } from '@Icons/additional/arrow-left.svg';
import { useHeaderCaption } from '@Hooks/useHeaderCaption';
import { FormType, HeaderStepperOptions } from '@Components/MainForm/types';
import { Balance } from '@Types/api/form/leaveRequest/Balance';

import RequisitionInformation from './components/RequisitionInformation/RequisitionInformation';
import FooterButtonSection from './components/FooterButtonSection/FooterButtonSection';
import styles from './main-form-mobile.module.css';
import InformationSidebar from '../components/InformationSidebar/InformationSidebar';
import HeaderStepper from '../components/HeaderStepper/HeaderStepper';

interface MainFormMobileProps {
  userInfo: User;
  requisitionInfo: RequisitionInfo;
  formType: FormType;
  selectedUser?: User;
  disabledSaveForm?: boolean;
  disabledNextStep?: boolean;
  descriptionAlert?: JSX.Element;
  showSelectEmployee?: boolean;
  showLeaveBalance?: boolean;
  layout?: FormProps['layout'];
  children?: ReactNode;
  className?: string;
  handleSubmit?: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
  handleSearchEmpByNumber?: (empNumber?: string) => void;
  onFooterAction: (action: FooterActions, payload?: string) => void;
  showExtraUserFields?: boolean;
  showMRsection?: boolean;
  balance?: Balance;
  headerStepperOptions?: HeaderStepperOptions;
  requestHeaderStep?: boolean;
  approveBtnName?: string;
}

const { Content } = Layout;

const MainFormMobile: React.FC<MainFormMobileProps> = ({
  handleSubmit,
  children,
  className,
  layout = 'vertical',
  userInfo,
  selectedUser,
  requisitionInfo,
  showLeaveBalance = false,
  showSelectEmployee = false,
  disabledSaveForm = false,
  disabledNextStep = true,
  onFooterAction,
  handleSearchEmpByNumber,
  descriptionAlert,
  formType,
  showExtraUserFields = false,
  showMRsection = false,
  requestHeaderStep,
  headerStepperOptions,
  balance,
  approveBtnName,
}) => {
  const headerCaption = useHeaderCaption(true);

  const handleBack = (): void => onFooterAction(FooterActions.BACK_TO_REQUESTS);
  return (
    <Layout className={styles.layoutContainer}>
      <Layout>
        <Content className={styles.formContent}>
          <div className={classNames('trojan-sticky', styles.formBackSection)}>
            <NextArrowIcon onClick={handleBack} />
            <span>{headerCaption}</span>
          </div>
          <div className={styles.headerStepperWrapper}>
            {headerStepperOptions && (
              <HeaderStepper options={headerStepperOptions} />
            )}
          </div>
          <div style={{ position: 'relative', paddingBottom: '16px' }}>
            <RequisitionInformation
              requisitionDate={requisitionInfo.requisitionDate}
              requisitionNo={requisitionInfo.requisitionNo}
              requisitionStatus={requisitionInfo.requisitionStatus}
            />
          </div>
          <Form
            className={classNames(styles.form, className)}
            layout={layout}
            onFinish={handleSubmit}
            requiredMark={false}
          >
            <div
              className={classNames(
                headerStepperOptions
                  ? styles.formContentWithHeaderStepper
                  : styles.formContent
              )}
            >
              {children}
              {descriptionAlert && (
                <div>
                  <Informative description={descriptionAlert} />
                </div>
              )}
              {formType === 'approval' && (
                <MTextArea
                  placeholder="Leave a comment"
                  onChange={(event) => {
                    onFooterAction(
                      FooterActions.COMMENTS_CHANGE,
                      event.target.value
                    );
                  }}
                  showCount
                  style={{ height: 120 }}
                  maxLength={100}
                />
              )}
            </div>
            <div className={classNames(styles.formFooterContent)}>
              <div className={styles.informationContainer}>
                <InformationSidebar
                  showMRsection={showMRsection}
                  userInfo={userInfo}
                  selectedUser={selectedUser}
                  showLeaveBalance={showLeaveBalance}
                  balance={balance}
                  showSelectEmployee={showSelectEmployee}
                  handleSearchEmpByNumber={handleSearchEmpByNumber}
                  showExtraUserFields={showExtraUserFields}
                  requestHeaderStep={requestHeaderStep}
                />
                <FooterButtonSection
                  disabledNextStep={disabledNextStep}
                  disabledSaveForm={disabledSaveForm}
                  formType={formType}
                  onFooterAction={onFooterAction}
                  approveBtnName={approveBtnName}
                />
              </div>
            </div>
          </Form>
        </Content>
      </Layout>
    </Layout>
  );
};

export default memo(MainFormMobile);
